import { getCustomers } from "@domain/customer/list"
import { useQuery } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"
import type { GetCustomerOwnersData } from "__generated__/graphql"
import { useMemo } from "react"

export function customerLabel(item: GetCustomerOwnersData): string {
  return (
    item.customer?.companyName?.trim() ||
    `${item.customerPerson?.firstName ?? ""} ${
      item.customerPerson?.lastName ?? ""
    }`.trim() ||
    item.customerPerson?.email?.trim() ||
    item.customer?.email?.trim() ||
    item.customer?._id ||
    ""
  )
}

export const CUSTOMERS_QUERY_KEY = ["customers-all-enabled"] as const

export function useCustomersQuery(enabled = true, includeDisabled = false) {
  const { data, isLoading } = useQuery({
    queryKey: CUSTOMERS_QUERY_KEY,
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const pageSize = 500
      const allDocs: GetCustomerOwnersData[] = []
      let pageNumber = 1
      let totalDocs = 0
      let hasMore = true

      while (hasMore) {
        const response = await getCustomers({ pageNumber, pageSize })
        if (!response.isSuccess) {
          showErrorToast("Müşteriler yüklenemedi")
          return { docs: [], totalDocs: 0 }
        }

        const docs = response.data.docs ?? []
        totalDocs = response.data.totalDocs ?? 0
        allDocs.push(...docs)

        hasMore = docs.length > 0 && allDocs.length < totalDocs
        pageNumber += 1
      }

      return { docs: allDocs, totalDocs }
    }
  })

  const customerRecords = useMemo(
    () =>
      (data?.docs ?? []).filter((item): item is GetCustomerOwnersData =>
        Boolean(item.customer?._id)
      ),
    [data?.docs]
  )

  const customerOptions = useMemo(
    () =>
      Array.from(
        new Map(
          customerRecords
            .filter(
              (item) =>
                includeDisabled || item.customer?.appStatus === "Enabled"
            )
            .map((item) => {
              const id = item.customer?._id || ""
              return [id, { id, label: customerLabel(item) }] as const
            })
        ).values()
      ).sort((a, b) => a.label.localeCompare(b.label, "tr")),
    [customerRecords, includeDisabled]
  )

  return { customerOptions, customerRecords, isLoading }
}
